import React, { useState } from "react";
import { FolderPlus, Loader2, Check, FolderKanban } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";

/** مشاريع التوليد الفردي المسمّاة: اختر مشروعًا محفوظًا بمدخلاته أو أنشئ مشروعًا جديدًا باسم مختلف */
export const StudioProjectBar = ({ list, value, onChange, onCreated, required = false }) => {
  const [adding, setAdding] = useState(list.length === 0);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (!name.trim()) return toast.error("اكتب اسم مشروع التوليد الفردي");
    setBusy(true);
    try {
      const { data } = await api.post("/me/studio-projects", { name: name.trim() });
      onCreated(data);
      setName(""); setAdding(false);
      toast.success(`أُنشئ مشروع «${data.name}» — سيُحفظ به عملك ومدخلاتك. اختر الآن نوع التوليد وأدخل بياناتك`);
    } catch (e) { toast.error(formatError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className={`glass rounded-2xl p-4 border ${required ? "border-accent/60 bg-accent/5 guide-glow" : "border-primary/30 bg-primary/5"}`} data-testid="studio-project-bar">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <FolderKanban className={`w-4 h-4 ${required ? "text-accent" : "text-primary"}`} />
        <h2 className="font-bold text-sm">{required ? "قبل البدء: أنشئ مشروعك وسمِّه" : "مشروع التوليد الفردي"}</h2>
        {!required && <span className="text-[11px] text-muted-foreground">كل مشروع يحتفظ بمدخلاته (الاسم التجاري، الشعار، المعلومات، المنتج) ويُحفظ محتواه باسمه في المكتبة</span>}
      </div>
      {required && (
        <ol className="text-xs text-muted-foreground mb-3 space-y-1 list-decimal pr-5" data-testid="studio-project-required-note">
          <li><b className="text-foreground">اكتب اسم مشروعك</b> (اسم نشاطك أو علامتك) — به يُحفظ كل عملك: مدخلاتك، شعارك، وكل ما تولّده في مكتبة المحتوى.</li>
          <li>بعد الإنشاء اختر <b className="text-foreground">نوع التوليد</b> وأدخل بياناتك، ثم ابدأ التوليد.</li>
          <li>يمكنك إنشاء أكثر من مشروع لاحقًا والتنقل بينها — مدخلات كل مشروع تُستعاد عند اختياره.</li>
        </ol>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {list.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">المشروع الحالي:</span>
            <Select value={value || ""} onValueChange={onChange}>
              <SelectTrigger className="w-[240px] bg-secondary border-border" data-testid="studio-project-select"><SelectValue placeholder="اختر مشروعًا" /></SelectTrigger>
              <SelectContent className="bg-popover border-border">
                {list.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`studio-project-option-${p.id}`}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {!adding && <Button size="sm" onClick={() => setAdding(true)} className="gap-1 font-bold" data-testid="studio-project-new-btn"><FolderPlus className="w-4 h-4" /> إنشاء مشروع جديد</Button>}
      </div>
      {adding && (
        <div className={`mt-2 rounded-xl border p-3 space-y-2 ${required ? "border-accent/50 bg-background/60" : "border-primary/40 bg-background/60"}`} data-testid="studio-project-create-box">
          {!required && <p className="text-xs text-muted-foreground"><b className="text-foreground">مشروع جديد:</b> اكتب اسم النشاط أو العلامة — سيُحفظ به شعارك ومدخلاتك وكل ما تولّده له في مكتبة المحتوى، وتتنقل بينه وبين مشاريعك الأخرى من القائمة.</p>}
          <div className="flex flex-wrap items-center gap-2">
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} autoFocus placeholder={required ? "اسم مشروعك — مثال: شاورما شفيق" : "اسم المشروع الجديد — مثال: مطعم الذواقة"} className="bg-secondary border-border flex-1 min-w-[220px]" data-testid="studio-project-name-input" onKeyDown={(e) => e.key === "Enter" && create()} />
            <Button size="sm" onClick={create} disabled={busy || !name.trim()} className="gap-1 font-bold" data-testid="studio-project-create-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {required ? "إنشاء والبدء" : "إنشاء المشروع"}</Button>
            {list.length > 0 && <Button size="sm" variant="ghost" onClick={() => { setAdding(false); setName(""); }} data-testid="studio-project-cancel-btn">إلغاء</Button>}
          </div>
        </div>
      )}
    </div>
  );
};
